import { useQuery } from '@apollo/client'
import {
  ascend,
  assoc,
  filter,
  find,
  identity,
  map,
  memoizeWith,
  pluck,
  sort,
  take,
  uniq,
} from 'ramda'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { GetOrderProductsDocument } from '../../../../graphql/magento'
import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../../layouts/context'
import { ReorderContext, SortedItem } from '../../../../lib/orders/context'
import { isProductNotNil, skuProp, skuPropEquals } from '../../../../lib/orders/utils'
import { isMagentoProductsQueryConfiguredSimpleProduct } from '../../../../lib/products'
import Spinner from '../../../Spinner'
import { ReorderItemsEntry, ReorderItemsEntryProps } from './ReorderItemsEntry'

const StyledItemsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: row;
  gap: 16px;

  ${ReorderItemsEntry} {
    padding: 20px;
  }
`

export interface ReorderItemsProps {
  className?: string
  showFrequentItems: boolean
  showRecentItems: boolean
}

const ReorderItems: React.FC<ReorderItemsProps> = ({
  className,
  showFrequentItems,
  showRecentItems,
}) => {
  const { itemsByDate, itemsByCount, loading } = useContext(ReorderContext)
  const token = useContext(TokenContext)
  const [{ resolvedItemsByCount, resolvedItemsByDate }, setResolvedItems] = useState<{
    resolvedItemsByCount: ReorderItemsEntryProps['item'][]
    resolvedItemsByDate: ReorderItemsEntryProps['item'][]
  }>({ resolvedItemsByCount: [], resolvedItemsByDate: [] })

  const skusToQuery = useMemo(() => {
    if (loading) {
      return undefined
    }
    const pluckSku = pluck('sku')
    const uniqueSkus = uniq([...pluckSku(itemsByDate), ...pluckSku(itemsByCount)])
    // sort skus for deterministic arguments
    return sort(ascend(identity), uniqueSkus)
  }, [itemsByCount, itemsByDate, loading])

  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useQuery(GetOrderProductsDocument, {
    context: { token },
    variables: {
      sku: skusToQuery,
    },
    skip: !skusToQuery,
    // return partial data if available
    errorPolicy: 'all',
  })

  useLogAndCaptureError(productsError)

  useEffect(() => {
    if (productsLoading) {
      return
    }
    const products = productsData?.products?.items
    if (!products) {
      setResolvedItems({
        resolvedItemsByCount: [],
        resolvedItemsByDate: [],
      })
      return
    }

    const filteredProducts = filter(isMagentoProductsQueryConfiguredSimpleProduct, products)
    const findProductForItem = memoizeWith(skuProp, (item: SortedItem) =>
      find(skuPropEquals(item), filteredProducts),
    )

    const addProductToItem = (item: SortedItem) => assoc('product', findProductForItem(item), item)
    const addProductsToItemsAndFilter = (items: SortedItem[]) =>
      filter(isProductNotNil, map(addProductToItem)(items))

    setResolvedItems({
      resolvedItemsByCount: addProductsToItemsAndFilter(itemsByCount),
      resolvedItemsByDate: addProductsToItemsAndFilter(itemsByDate),
    })
  }, [itemsByCount, itemsByDate, productsData?.products?.items, productsLoading])

  if (loading) {
    return <Spinner loading />
  }

  return (
    <div className={className}>
      {showFrequentItems && (
        <>
          <h3 className="small-h3">Most Frequently Purchased</h3>
          <StyledItemsContainer>
            {take(5, resolvedItemsByCount).map((item) => (
              <ReorderItemsEntry key={item.sku} item={item} />
            ))}
          </StyledItemsContainer>
          {productsLoading && <Spinner loading />}
          {showRecentItems && <hr />}
        </>
      )}
      {showRecentItems && (
        <>
          <h3 className="small-h3">Most Recently Purchased</h3>
          <StyledItemsContainer>
            {resolvedItemsByDate.map((item) => (
              <ReorderItemsEntry key={item.sku} item={item} />
            ))}
          </StyledItemsContainer>
          {productsLoading && <Spinner loading />}
        </>
      )}
    </div>
  )
}

ReorderItems.defaultProps = {
  className: '',
}

const StyledReorderRecentItems = styled(ReorderItems)({
  marginBottom: '30px',
  paddingTop: '10px',
})

export default StyledReorderRecentItems
